import * as React from 'react';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {LoadingText} from 'shells/loading-placeholder/loading-text';
import {SelectPickerLabel} from 'shells/select-picker-label/select-picker-label';
import type {SelectOption} from 'shells/select-picker-label/select-picker-label';
import {Body} from 'shells/typography/body';

import {useGetMarkets} from '../../../../core/select/hooks';

import {useGetMarketForLead} from '../graphql/hooks/use-get-market-for-lead';
import {useUpdateMarketForLead} from '../graphql/hooks/use-update-market-for-lead';

type Props = {
    leadId: string;
    setError: (error?: string | null | undefined) => void;
};

export function MarketSelector(props: Props) {
    const {market: currentMarket, isLoading: isLoadingMarket} = useGetMarketForLead(props.leadId);
    const {markets, isLoading: isLoadingMarkets} = useGetMarkets();

    const {updateMarketForLead} = useUpdateMarketForLead();

    if (currentMarket && markets != null) {
        const marketOptions: SelectOption[] = markets.map((market) => ({
            label: `${market.name} (${market.currency})`,
            value: market.id,
        }));

        const selectedOption: SelectOption = {
            label: `${currentMarket.name} (${currentMarket.currency})`,
            value: currentMarket.id,
        };

        const onChange = (option: SelectOption) => {
            const newMarket = markets.find((market) => market.id === option.value);
            if (newMarket == null) {
                // This should be impossible, but it's needed to satisfy Flow
                throw new Error(`Market with id ${option.value} not found`);
            }

            updateMarketForLead(props.leadId, newMarket).catch((e) => {
                props.setError(safelyGetGraphQLErrorMessage(e));
            });
        };

        return (
            <SelectPickerLabel
                options={marketOptions}
                selectedOption={selectedOption}
                onChange={onChange}
                color='blue'
                size='small'
            />
        );
    }

    if (isLoadingMarket || isLoadingMarkets) {
        return <LoadingText width={100} />;
    }

    return (
        <Body color='error' size='tiny'>
            Market failed to load
        </Body>
    );
}
